import { useEffect, useState, useRef } from 'react';
import { AlertTriangle, Mail, ExternalLink, Info, AlertCircle, Clock } from 'lucide-react';
import tenantStatusService, { TenantStatusResponse } from '@/services/tenant-status.service';
import { SeverityLevel } from '@/types/tenant.types';

/**
 * Tenant Blocked Page
 *
 * Displayed when a tenant status is INACTIVE or SUSPENDED.
 * Shows clear messaging and contact options for users.
 * Supports custom suspension messages with severity levels.
 */
export default function TenantBlocked() {
  const [statusInfo, setStatusInfo] = useState<TenantStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const statusRef = useRef<string | undefined>(undefined);
  const timerInitializedRef = useRef<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const info = await tenantStatusService.checkStatus();
        if (!isMounted) return;

        setStatusInfo(info);
        statusRef.current = info.status;

        // If tenant is now active, redirect to login
        if (info.can_login) {
          console.log('[TenantBlocked] Tenant is now active, redirecting to login');
          window.location.replace('/login');
          return;
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('Failed to load tenant status:', error);
        // Set default error state
        setStatusInfo({
          tenant_key: 'unknown',
          status: 'INACTIVE',
          can_login: false,
          message: 'This organization account is currently unavailable.',
        });
        statusRef.current = 'INACTIVE';
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Silent check without updating loading state - used for polling
    const pollStatus = async () => {
      if (!isMounted) return;

      try {
        const info = await tenantStatusService.checkStatus();
        if (!isMounted) return;

        // If tenant is now active, redirect to login
        if (info.can_login) {
          console.log('[TenantBlocked] Tenant has been unblocked, redirecting to login');
          window.location.replace('/login');
          return;
        }

        // Only update if status actually changed (avoid re-renders that reset the timer)
        if (statusRef.current !== info.status) {
          setStatusInfo(info);
          statusRef.current = info.status;
        }
      } catch (error) {
        console.error('Failed to check tenant status:', error);
        // Don't update state on error during polling - keep showing existing info
      }
    };

    fetchStatus();

    // Poll tenant status every 5 seconds to detect if tenant is unblocked
    const statusPollInterval = setInterval(pollStatus, 5000);

    return () => {
      isMounted = false;
      clearInterval(statusPollInterval);
    };
  }, []);

  // Update countdown timer for expiry using seconds_remaining
  // Use seconds_remaining as dependency, not the entire statusInfo object
  const secondsRemaining = statusInfo?.suspension_config?.seconds_remaining;

  useEffect(() => {
    if (!secondsRemaining) return;

    // Prevent duplicate timer initialization (React StrictMode / re-renders)
    // Only reinitialize if seconds_remaining actually changed
    if (timerInitializedRef.current === secondsRemaining) return;
    timerInitializedRef.current = secondsRemaining;

    let remainingSeconds = secondsRemaining;

    const updateTimer = () => {
      if (remainingSeconds <= 0) {
        setTimeRemaining('Expiry time reached - refreshing...');
        setTimeout(() => window.location.reload(), 2000);
        return;
      }

      const hours = Math.floor(remainingSeconds / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;

      if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`);
      } else {
        setTimeRemaining(`${seconds}s`);
      }

      remainingSeconds--;
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000); // Update every second

    return () => clearInterval(interval);
  }, [secondsRemaining]);

  const getSeverityConfig = (severity?: SeverityLevel) => {
    switch (severity) {
      case 'info':
        return {
          bgGradient: 'from-blue-50 to-cyan-50',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          icon: Info,
          borderColor: 'border-blue-200',
          textColor: 'text-blue-900',
          accentColor: 'text-blue-600'
        };
      case 'critical':
        return {
          bgGradient: 'from-red-50 to-pink-50',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          icon: AlertCircle,
          borderColor: 'border-red-200',
          textColor: 'text-red-900',
          accentColor: 'text-red-600'
        };
      case 'warning':
      default:
        return {
          bgGradient: 'from-orange-50 to-yellow-50',
          iconBg: 'bg-orange-100',
          iconColor: 'text-orange-600',
          icon: AlertTriangle,
          borderColor: 'border-orange-200',
          textColor: 'text-orange-900',
          accentColor: 'text-orange-600'
        };
    }
  };

  const getStatusConfig = () => {
    // If we have custom suspension config, use its severity
    if (statusInfo?.suspension_config) {
      return getSeverityConfig(statusInfo.suspension_config.severity);
    }

    // Default configs for standard statuses
    if (!statusInfo) {
      return getSeverityConfig('critical');
    }

    switch (statusInfo.status) {
      case 'INACTIVE':
        return getSeverityConfig('info');
      case 'SUSPENDED':
        return getSeverityConfig('warning');
      default:
        return getSeverityConfig('warning');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const config = getStatusConfig();
  const IconComponent = config.icon;
  const suspensionConfig = statusInfo?.suspension_config;

  // Use custom message if available, otherwise use default
  const displayMessage = suspensionConfig?.custom_message || statusInfo?.message || 'This organization account is currently unavailable.';

  return (
    <div className={`min-h-screen flex items-center justify-center bg-gradient-to-br ${config.bgGradient} px-4`}>
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          {/* Header with Icon */}
          <div className={`${config.iconBg} px-8 py-6 border-b ${config.borderColor}`}>
            <div className="flex justify-center mb-4">
              <div className={`w-20 h-20 ${config.iconBg} rounded-full flex items-center justify-center shadow-inner ring-4 ring-white`}>
                <IconComponent className={`w-10 h-10 ${config.iconColor}`} />
              </div>
            </div>
            <h1 className={`text-3xl font-bold ${config.textColor} text-center`}>
              {statusInfo?.status === 'SUSPENDED' ? 'Temporarily Unavailable' : 'Account Inactive'}
            </h1>
            {suspensionConfig?.severity && (
              <div className="flex justify-center mt-3">
                <span className={`px-3 py-1 ${config.iconBg} ${config.iconColor} text-xs font-semibold rounded-full uppercase`}>
                  {suspensionConfig.severity}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            {/* Main Message */}
            <div className={`${config.iconBg} border ${config.borderColor} rounded-lg p-5 mb-6`}>
              <p className={`${config.textColor} text-center text-base leading-relaxed whitespace-pre-line`}>
                {displayMessage}
              </p>
            </div>

            {/* Expiry Countdown */}
            {suspensionConfig?.seconds_remaining && timeRemaining && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Auto-Unblock Scheduled</p>
                    <p className="text-sm text-blue-700">
                      Service will resume in <strong>{timeRemaining}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            {(suspensionConfig?.contact_email || suspensionConfig?.contact_link) && (
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-semibold text-gray-700">Need Assistance?</h3>
                {suspensionConfig.contact_email && (
                  <a
                    href={`mailto:${suspensionConfig.contact_email}`}
                    className="flex items-center gap-3 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all group"
                  >
                    <Mail className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email Support</p>
                      <p className="text-xs text-gray-500">{suspensionConfig.contact_email}</p>
                    </div>
                  </a>
                )}
                {suspensionConfig.contact_link && (
                  <a
                    href={suspensionConfig.contact_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all group"
                  >
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Support Center</p>
                      <p className="text-xs text-gray-500">Visit our help portal</p>
                    </div>
                  </a>
                )}
              </div>
            )}

            {/* Default Help (when no custom contact info) */}
            {!suspensionConfig?.contact_email && !suspensionConfig?.contact_link && (
              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg p-5 mb-6">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">What can you do?</h3>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>Contact your organization administrator for assistance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-400 mt-0.5">•</span>
                    <span>Reach out to SkillZen support team for help</span>
                  </li>
                  {statusInfo?.status === 'INACTIVE' && (
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>Check if there are any billing or compliance issues</span>
                    </li>
                  )}
                  {statusInfo?.status === 'SUSPENDED' && !suspensionConfig && (
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-0.5">•</span>
                      <span>Review terms of service and verify account compliance</span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {/* Organization Info - Only show if enabled in config */}
            {statusInfo?.tenant_key &&
             statusInfo.tenant_key !== 'unknown' &&
             (suspensionConfig?.show_organization_details !== false) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Organization ID: <span className="font-mono font-medium text-gray-700">{statusInfo.tenant_key}</span>
                </p>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Status: <span className={`font-medium ${statusInfo.status === 'SUSPENDED' ? 'text-red-600' : 'text-gray-600'}`}>
                    {statusInfo.status}
                  </span>
                </p>
                {suspensionConfig?.blocked_at && (
                  <p className="text-xs text-gray-500 text-center mt-1">
                    Since: {new Date(suspensionConfig.blocked_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 text-center border-t border-gray-200">
            <p className="text-xs text-gray-600">
              {suspensionConfig?.seconds_remaining
                ? 'This is a temporary suspension. Service will resume automatically.'
                : 'Need immediate assistance? Contact your administrator.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
